import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {DataObjectId} from 'yougile-model'
import {DragData} from '@common/drag-data'
import {Icon} from '@components/common/base/icon'
import {
  TwColumnsWrapper,
  TwDragIconWrapper,
  TwOverlay,
  TwTableRowInner
} from '@components/common/base/table/containers'
import {TableCell} from '@components/common/base/table/table-cell'
import {useTableStore} from '@components/common/base/table/table-store'
import {
  TableDataType,
  TableRow as TableRowType
} from '@components/common/base/table/types'
import {cn} from '@containers/html-utils'

export const TableRow = observer(
  <T extends TableDataType>({
    rowIndex,
    data,
    parentId,
    subRows,
    collapsable = false,
    subRowLevel = 0,
    onClick,
    draggable = false,
    hideSubRowsOnDrag = false,
    isDraggableOut = false,
    hoverable = false,
    columnsWrapperClassName = '',
    draggingStyle = false,
    noBorders = false,
    className,
    innerClassName,
    highlightRowsAsDropzone = true
  }: {
    rowIndex: string
    data: T
    parentId?: DataObjectId
    draggable?: boolean
    isDraggableOut?: boolean
    hoverable?: boolean
    subRows?: TableRowType<T>[]
    hideSubRowsOnDrag?: boolean
    collapsable?: boolean
    subRowLevel?: number
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
    columnsWrapperClassName?: string
    draggingStyle?: boolean
    noBorders?: boolean
    className?: string
    innerClassName?: string
    highlightRowsAsDropzone?: boolean
  }) => {
    const [isDropZone, setIsDropZone] = useState(false)
    const [state, store] = useTableStore()
    const emptySpace = subRowLevel * 24
    const isTopParentRowHighlighted =
      !!subRowLevel && state.highlightedRows?.includes(rowIndex.split('.')[0])

    // Prevent render when another drag type that is handled in components above is used
    const isDraggingOver = store.isDragTypeOver && !!state.draggingRowId

    useEffect(() => {
      if (store.isDragTypeOver && isDropZone) {
        setIsDropZone(false)
      }
    }, [isDraggingOver, state.allRows.length])

    const columnWidth = (
      width: number | undefined | 'none',
      columnNumber: number
    ) => {
      if (width === 'none') {
        return undefined
      }
      return width && columnNumber === 0 ? width - emptySpace : width
    }

    const onDragOver = () => {
      const isDragging = !!DragData.get()
      if (
        store.isDragTypeOver &&
        (!!state.draggingRowId || isDragging) &&
        !isDropZone &&
        highlightRowsAsDropzone
      ) {
        setIsDropZone(true)
      }
    }

    const onDragLeave = () => {
      if (store.isDragTypeOver && isDropZone) {
        setIsDropZone(false)
      }
    }

    const managedHoverProps = store.onRowHover
      ? {
          $managedHover: true,
          $hovered: data.id === state.hoveredRowId,
          onMouseMove: () => store.onRowHovered(data.id),
          onMouseLeave: () => store.onRowHovered(undefined)
        }
      : {}

    return (
      <TwTableRowInner
        style={{width: subRowLevel ? `calc(100% - 24px)` : 'auto'}}
        $subRowLevel={subRowLevel}
        $draggable={draggable}
        $hoverable={hoverable || isDraggableOut || Boolean(onClick)}
        $highlighted={Boolean(
          isTopParentRowHighlighted || state.highlightedRows?.includes(rowIndex)
        )}
        $isDragging={isDraggingOver}
        {...managedHoverProps}
        $isDropZone={isDropZone}
        $noBorders={noBorders}
        onClick={onClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={className}
        data-row-id={data.id}
      >
        {store.isDragTypeOver && isDropZone && <TwOverlay />}
        <TwColumnsWrapper
          $isDragging={isDraggingOver}
          className={cn(columnsWrapperClassName, innerClassName)}
        >
          {subRowLevel === 0 && (draggable || isDraggableOut) && (
            <TwDragIconWrapper>
              <Icon icon="IconCustomDrag" />
            </TwDragIconWrapper>
          )}
          {store.currentColumns.map((column, i) => (
            <TableCell
              key={i}
              width={columnWidth(column.width, i)}
              minWidth={columnWidth(column.minWidth, i)}
              maxWidth={columnWidth(column.maxWidth, i)}
              className={cn(column.className, column.cellClassName)}
              hideOnDrag={column.hideOnDrag}
            >
              {column.render(data, {
                collapsable,
                rowIndex,
                parentId
              })}
            </TableCell>
          ))}
        </TwColumnsWrapper>
        {!!subRows?.length &&
          (!collapsable || state.expanded.has(rowIndex)) &&
          subRows.map((subRow, i) => {
            return (
              <TableRow
                key={i}
                rowIndex={`${rowIndex}.${i}`}
                subRowLevel={subRowLevel + 1}
                parentId={subRow.parentId}
                data={subRow.data}
                collapsable={subRow.collapsable}
                subRows={subRow.subRows}
                onClick={subRow.onClick}
                draggable={draggable}
                hoverable={draggable || subRow.hoverable}
                noBorders={noBorders}
                columnsWrapperClassName={cn(
                  columnsWrapperClassName,
                  hideSubRowsOnDrag && 'hide-on-drag'
                )}
                draggingStyle={draggingStyle || draggable}
              />
            )
          })}
      </TwTableRowInner>
    )
  }
)
